import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { smoothScrollTo } from "@/lib/scroll";
import { ThemeProvider } from "@/lib/theme";
import { DemoProvider } from "@/lib/demoState";
import { ConsentProvider } from "@/lib/consentContext";
import { AmbientLight } from "@/components/motion/AmbientLight";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { CookieConsent } from "@/components/legal/CookieConsent";
import Landing from "@/pages/Landing";
import PolicyPage from "@/pages/PolicyPage";
import PoliciesPage from "@/pages/PoliciesPage";
import ContactPage from "@/pages/ContactPage";
import DownloadPage from "@/pages/DownloadPage";
import WaitlistPage from "@/pages/WaitlistPage";
import { AboutPage, HumnAIPage, CreditsPage, SafetyPage, NotFoundPage } from "@/pages/InfoPages";
import FeaturesPage from "@/pages/FeaturesPage";
import ExperiencePage from "@/pages/ExperiencePage";
import SlmTechnologyPage from "@/pages/SlmTechnologyPage";
import IntentionJourneyPage from "@/pages/IntentionJourneyPage";
import InvestorPitchPage from "@/pages/InvestorPitchPage";
import MemoryCreatorPage from "@/pages/MemoryCreatorPage";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => smoothScrollTo(document.querySelector(hash)), 120);
    } else {
      window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

const useLenis = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
};

function Shell() {
  useLenis();
  const { pathname } = useLocation();
  const isolatedJourney = pathname === "/intention";
  const isolatedInvestor = pathname.startsWith("/invest");
  const isolatedMemory = pathname === "/memory";
  const isolatedRoute = isolatedJourney || isolatedInvestor || isolatedMemory;
  return (
    <div className="grain relative">
      {!isolatedRoute && <AmbientLight />}
      <ScrollToTop />
      {!isolatedRoute && <Navigation />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/humn-ai" element={<HumnAIPage />} />
        <Route path="/slm" element={<SlmTechnologyPage />} />
        <Route path="/experience" element={<ExperiencePage />} />
        <Route path="/intention" element={<IntentionJourneyPage />} />
        <Route path="/invest/*" element={<InvestorPitchPage />} />
        <Route path="/memory" element={<MemoryCreatorPage />} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="/safety" element={<SafetyPage />} />
        <Route path="/support" element={<ContactPage kind="support" />} />
        <Route path="/contact" element={<ContactPage kind="contact" />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/waitlist" element={<WaitlistPage />} />
        <Route path="/policies" element={<PoliciesPage />} />
        <Route path="/:slug" element={<PolicyPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {pathname !== "/waitlist" && !isolatedRoute && <Footer />}
      {!isolatedMemory && <CookieConsent />}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <DemoProvider>
        <BrowserRouter>
          <ConsentProvider>
            <Shell />
          </ConsentProvider>
        </BrowserRouter>
      </DemoProvider>
    </ThemeProvider>
  );
}

export default App;
